import { useCallback, useMemo, useRef, useState } from "react";
import { showToast } from "services/utils/Status";

// Form state shared by both Punch Processing screens: the edited values, the
// last saved copy (to know whether anything changed), validation errors, and
// the Save / Reset to Default handlers.
//
// Screens can supply onSave to persist values. Preview-only consumers keep
// the local save behavior, while API failures leave edits unsaved.
// successMessage may be a function, called after onSave, so a screen can show
// the message the server sent back.
// toastOnInvalid: false skips the "fix the highlighted fields" toast, for
// screens where every validated field already shows its own inline error.
export const useSettingsForm = ({ buildDefaults, validate, resetMessage, onSave, successMessage, resetMarksSaved = true, toastOnInvalid = true }) => {
  const [saved, setSaved] = useState(buildDefaults);
  const [form, setForm] = useState(buildDefaults);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const replaceValues = useCallback(values => {
    setForm(values);
    setSaved(values);
    setErrors({});
  }, []);

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(saved),
    [form, saved],
  );

  // setValue("retention.hot", next) -> form.retention.hot = next
  const setValue = useCallback((path, value) => {
    if (savingRef.current) return;
    const keys = path.split(".");
    setForm((previous) => {
      const next = { ...previous };
      let cursor = next;
      keys.slice(0, -1).forEach((key) => {
        cursor[key] = { ...cursor[key] };
        cursor = cursor[key];
      });
      cursor[keys[keys.length - 1]] = value;
      return next;
    });
    setErrors((previous) => (previous[path] ? { ...previous, [path]: "" } : previous));
  }, []);

  const save = useCallback(async () => {
    if (savingRef.current) return false;
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      if (toastOnInvalid) showToast("error", "Please fix the highlighted fields before saving.");
      return false;
    }
    savingRef.current = true;
    setIsSaving(true);
    try {
      const result = onSave ? await onSave(form) : form;
      if (result === false) return false;
      replaceValues(result || form);
      const message = typeof successMessage === "function" ? successMessage() : successMessage;
      showToast("success", message || "Changes saved for this preview only. The settings service is not connected yet.");
      return true;
    } catch (error) {
      showToast("error", error.message || "Settings could not be saved.");
      return false;
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [form, validate, onSave, successMessage, replaceValues, toastOnInvalid]);

  // Drop unsaved edits and go back to the last saved values.
  const discard = useCallback(() => {
    setForm(saved);
    setErrors({});
  }, [saved]);

  const reset = useCallback(() => {
    if (savingRef.current) return;
    const defaults = buildDefaults();
    setForm(defaults);
    if (resetMarksSaved) setSaved(defaults);
    setErrors({});
    showToast("success", resetMessage || "Settings reset to default.");
  }, [buildDefaults, resetMessage, resetMarksSaved]);

  return { form, errors, isDirty, isSaving, setValue, save, discard, reset, replaceValues };
};

export default useSettingsForm;
